/**
 * @file listeners to handle window/visualViewport resizes
 */
import { browserCan } from 'bits/browser'
import window from 'bits/global'

type observeWindowSizeHandler = (dimensions: { height: number; width: number }) => void
export function observeWindowSize(handler: observeWindowSizeHandler): (() => void) | null {
  if (!handler) return null

  function listener(ev: Event): void {
    if (ev.target !== window) return
    const dimensions = {
      height: window.innerHeight,
      width: window.innerWidth,
    }
    handler(dimensions)
  }

  window.addEventListener('resize', listener)
  const unobserveWindowSize = (): void => window.removeEventListener('resize', listener)
  return unobserveWindowSize
}

type observeVisualViewportSizeHandler = (dimensions: { height: number; width: number; scale: number }) => void
export function observeVisualViewportSize(handler: observeVisualViewportSizeHandler): (() => void) | null {
  if (!handler) return null
  if (!browserCan('visualViewport')) return null

  function listener(ev: Event): void {
    if (ev.target !== window.visualViewport) return
    const dimensions = {
      height: window.visualViewport.height,
      width: window.visualViewport.width,
      scale: window.visualViewport.scale,
    }
    handler(dimensions)
  }

  // According to the example at https://developer.mozilla.org/en-US/docs/Web/API/Visual_Viewport_API#example,
  // visual viewport also changes when the page is scrolled so we should listen to scroll event and handle cases where both resize and scroll events are fired at the same time
  // For now, we only need this to detect mobile devices virtual keyboard so resize is enough

  window.visualViewport.addEventListener('resize', listener)
  const unobserveWindowSize = (): void => window.visualViewport.removeEventListener('resize', listener)
  return unobserveWindowSize
}
